
import React from 'react'
import TabBar from 'smartstream-widgets/controls/TabBar/TabBar'
import { alpha  } from '@mui/material/styles'
import { compose } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'
import { competitionTranslations } from 'smartstream-widgets/constants/competitions'

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { PropTranslations } from 'smartstream-widgets/types/widget'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

type Props = {
  availableCompetitions: Array<string>,
  classes: {
    [K in string]: string
  },
  competitionId: string | null,
  mobileView: boolean,
  onTabChange: ((arg0: string) => void),
  translations: PropTranslations
}

const styles = (theme: Theme) =>  createStyles({
  root: {},
  selected: {
    '& $tabWrapper': {
      fontWeight: 'bold'
    }
  },
  arrowLeft: {
    left: 0
  },
  arrowRight: {
    right: 0
  },
  mobile: {
    background: theme.palette.primary.main,
    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14)',
    '& button': {
      color: theme.palette.primary.contrastText
    }
  },
  scrollButtons: {
    height: '95%',
    top: 0,
    position: 'absolute',
    zIndex: 5,
    '&:empty': {
      display: 'none'
    }
  },
  tabWrapper: {
    transition: 'color 250ms'
  },
  tabRoot: {
    '&:not($selected):hover': {
      color: theme.palette.primary.main
    }
  },
  tabRootMobile: {
    '&:not($selected):hover': {
      color: theme.palette.primary.contrastText
    }
  },
  tabBar: {
    position: 'relative',
    '& $scrollButtons:first-child': {
      background: `linear-gradient(to right, ${theme.palette.background.paper} 70%, ${alpha(theme.palette.background.paper, 0.0)})`,
      left: 0,
      '& svg': {
        color: theme.palette.action.active
      }
    },
    '& $scrollButtons:last-child': {
      background: `linear-gradient(to right, ${alpha(theme.palette.background.paper, 0.0)}, ${theme.palette.background.paper} 30%) `,
      right: 0,
      '& svg': {
        color: theme.palette.action.active
      }
    },
    '& button:first-child': {
      textTransform: 'capitalize'
    }
  },
  tabBarMobile: {
    position: 'relative',
    '& $scrollButtons:first-child': {
      background: `linear-gradient(to right, ${theme.palette.primary.main} 70%, ${alpha(theme.palette.primary.main, 0.0)})`,
      '& svg': {
        color: theme.palette.primary.contrastText
      }
    },
    '& $scrollButtons:last-child': {
      background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.0)}, ${theme.palette.primary.main} 30%) `,
      '& svg': {
        color: theme.palette.primary.contrastText
      }
    }
  },
  selectedMobile: {
    '&$selected': {
      color: theme.palette.primary.contrastText
    }
  },
  indicatorMobile: {
    background: theme.palette.primary.contrastText
  }
})

export function getCompetitionTabs (
  availableCompetitions: Array<string>,
  translations: PropTranslations
) {
  return availableCompetitions && availableCompetitions.length > 0 && availableCompetitions.reduce((tabs: Array<{ id: string, name: string }>, cp) => {
    let title: string
    if (cp === 'today') title = translations.today
    if (translations[cp]) title = translations[cp]
    tabs.push({
      id: cp,
      name: title || cp
    })
    return tabs
  }, [])
}

export function CompetitionsTabBar (props: Props) {
  const {
    classes,
    competitionId,
    availableCompetitions,
    mobileView,
    onTabChange,
    translations
  } = props
  const tabElements = getCompetitionTabs(availableCompetitions, translations)
  if (!tabElements) return null

  return (
    <TabBar
      appBarClasses={{ root: `${classes.root} ${mobileView ? classes.mobile : ''}` }}
      color='default'
      indicatorColor={'primary'}
      onTabChange={onTabChange}
      scrollButtons={true}
      tabElements={tabElements}
      textColor='primary'
      tabBarClasses={{
        indicator: `${mobileView ? classes.indicatorMobile : ''}`,
        scrollButtons: classes.scrollButtons,
        root: `${classes.tabBar} ${mobileView ? classes.tabBarMobile : ''}`
      }}
      tabClasses={{
        root: `${mobileView ? classes.tabRootMobile : classes.tabRoot}`,
        selected: `${classes.selected} ${mobileView ? classes.selectedMobile : ''}`,
        wrapper: classes.tabWrapper
      }}
      value={competitionId}
      variant='scrollable'
    />
  )
}

export default compose(
  withStyles(styles,{defaultTheme: getDefaultTheme}),
  withTranslations((t: TranslationFunction) => {
    return {
      today: t('today.translations', {
        defaultValue: 'Today',
        context: 'label tab for today in Competitions Tab Bar in Odds Comparison Grid Widget'
      }),
      ...competitionTranslations(t)
    }
  })
)(CompetitionsTabBar)
